import WebApp from '@twa-dev/sdk';
import { type Ref, ref } from 'vue';

interface useTelegramComposableState {
  showMainButton: (text: string, callback: () => void) => void;
  hideMainButton: () => void;
  showBackButton: (callback: () => void) => void;
  hideBackButton: () => void;
  setButtonLoader: (state: boolean) => void;
  showAlert: (text: string) => void;
  showConfirm: (message: string) => Promise<boolean>;
  openInvoice: (url: string, callback: (status: 'pending' | 'failed' | 'cancelled' | 'paid') => void) => void;
  closeApp: () => void;
  expand: () => void;
  getViewportHeight: () => number;
  vibrate: (style?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'error' | 'warning' | 'success') => void;
  ready: () => void;
  colorScheme: 'light' | 'dark' | undefined;
  platform: 'android' | 'android_x' | 'ios' | 'macos' | 'tdesktop' | 'web' | 'weba' | 'webk' | 'unigram' | 'unknown';
  headerColor: string;
  setHeaderColor: (color: 'bg_color' | 'secondary_bg_color' | `#${string}`) => void;
  setBackgroundColor: (color: 'bg_color' | 'secondary_bg_color' | `#${string}`) => void;
  setBottomBarColor: (color: 'bg_color' | 'secondary_bg_color' | 'bottom_bar_bg_color' | `#${string}`) => void;
  webAppInitData: string;
  openTelegramLink: (url: string) => void;
  shareMessage: (msgId: string, callback?: (success: boolean) => void) => void;
  openLink: (url: string) => void;
  disableVerticalSwipes: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
}

/**
 * Composable to work with Telegram using the Mini Apps SDK
 *
 * @see https://core.telegram.org/bots/webapps
 */
export default function useTelegram(): useTelegramComposableState {
  /**
   * We store current MainButton callback to be able to remove it later
   */
  const mainButtonCallback = ref<(() => void) | null>(null);

  /**
   * We store current BackButton callback to be able to remove it later
   */
  const backButtonCallback = ref<(() => void) | null>(null);

  const debugMainButton = ref<HTMLButtonElement | null>(null);
  const debugBackButton = ref<HTMLButtonElement | null>(null);

  /**
   * When we debug the app in the browser, we need to create a fake main button
   *
   * @param reference The reference to store the button
   * @param className The class name to add to the button
   */
  function prepareDebugButton(reference: Ref<HTMLButtonElement | null>, className: string): void {
    if (WebApp.platform !== 'unknown') {
      return;
    }

    if (reference.value !== undefined) {
      return;
    }

    const button = document.createElement('button');

    button.classList.add(className);
    document.body.appendChild(button);
    reference.value = button;
  }

  /**
   * Show the main button
   *
   * @param text The text to show on the button
   * @param callback The callback to call when the button is clicked
   */
  function showMainButton(text: string, callback: () => void): void {
    prepareDebugButton(debugMainButton, 'fake-main-button');

    if (mainButtonCallback.value !== null) {
      WebApp.MainButton.offClick(mainButtonCallback.value);
    }

    mainButtonCallback.value = callback;

    WebApp.MainButton.text = text ?? 'Submit';
    WebApp.MainButton.onClick(mainButtonCallback.value);
    WebApp.MainButton.isVisible = true;

    if (debugMainButton.value !== null) {
      debugMainButton.value.innerText = text ?? 'Submit';
      debugMainButton.value.addEventListener('click', mainButtonCallback.value);
      debugMainButton.value.classList.add('visible');
    }
  }

  /**
   * Hide the main button
   */
  function hideMainButton(): void {
    if (mainButtonCallback.value === null) {
      console.warn('Trying to hide main button but no callback was set');
      return;
    }

    WebApp.MainButton.offClick(mainButtonCallback.value);
    debugMainButton.value?.removeEventListener('click', mainButtonCallback.value);
    mainButtonCallback.value = null;

    WebApp.MainButton.isVisible = false;
    debugMainButton.value?.classList.remove('visible');
  }

  /**
   * Show the back button
   */
  function showBackButton(callback: () => void): void {
    prepareDebugButton(debugBackButton, 'fake-back-button');

    if (backButtonCallback.value !== null) {
      WebApp.BackButton.offClick(backButtonCallback.value);
    }

    backButtonCallback.value = callback;

    WebApp.BackButton.onClick(backButtonCallback.value);
    WebApp.BackButton.show();

    if (debugBackButton.value !== null) {
      debugBackButton.value.innerText = '‹ Back';
      debugBackButton.value.addEventListener('click', backButtonCallback.value);
      debugBackButton.value.classList.add('visible');
    }
  }

  /**
   * Hide the back button
   */
  function hideBackButton(): void {
    if (backButtonCallback.value === null) {
      console.warn('Trying to hide back button but no callback was set');
      return;
    }

    WebApp.BackButton.offClick(backButtonCallback.value);
    debugBackButton.value?.removeEventListener('click', backButtonCallback.value);
    backButtonCallback.value = null;

    WebApp.BackButton.hide();
    debugBackButton.value?.classList.remove('visible');
  }

  /**
   * Show/hide the main button loader
   *
   * @param state The state to set the loader to
   */
  function setButtonLoader(state: boolean): void {
    if (state) {
      WebApp.MainButton.showProgress();
    } else {
      WebApp.MainButton.hideProgress();
    }
  }

  /**
   * Shows native Telegram alert message
   *
   * @param text The text to show in the alert
   */
  function showAlert(text: string): void {
    WebApp.showAlert(text);
  }

  /**
   * Shows native Telegram confirm dialog. Resolves true if the user confirms.
   * Use this over window.confirm — native JS dialogs are unreliable in the
   * Telegram WebView.
   *
   * @param message The text to show in the confirm dialog
   */
  function showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      WebApp.showConfirm(message, (confirmed) => resolve(confirmed));
    });
  }

  /**
   * Opens Telegram invoice
   *
   * @param url The invoice URL
   * @param callback The callback to call when the invoice is paid
   */
  function openInvoice(url: string, callback: (status: 'pending' | 'failed' | 'cancelled' | 'paid') => void): void {
    WebApp.openInvoice(url, callback);
  }

  /**
   * Closes the app
   */
  function closeApp(): void {
    WebApp.close();
  }

  /**
   * Expands Telegram app layout
   */
  function expand(): void {
    WebApp.expand();
  }

  /**
   *
   * The current height of the visible area of the Mini App. Also available in CSS as the variable var(--tg-viewport-height).
   * The application can display just the top part of the Mini App, with its lower part remaining outside the screen area.
   * From this position, the user can "pull" the Mini App to its maximum height, while the bot can do the same by calling the expand() method.
   * As the position of the Mini App changes, the current height value of the visible area will be updated in real time.
   * Please note that the refresh rate of this value is not sufficient to smoothly follow the lower border of the window.
   * It should not be used to pin interface elements to the bottom of the visible area.
   * It's more appropriate to use the value of the viewportStableHeight field for this purpose.
   */
  function getViewportHeight(): number {
    return WebApp.viewportStableHeight;
  }

  /**
   * Vibrate the device
   *
   * @param style The style of the vibration
   */
  function vibrate(
    style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'error' | 'warning' | 'success' = 'heavy'
  ): void {
    switch (style) {
      case 'light':
      case 'medium':
      case 'heavy':
      case 'rigid':
      case 'soft':
        WebApp.HapticFeedback.impactOccurred(style);
        break;
      case 'error':
      case 'warning':
      case 'success':
        WebApp.HapticFeedback.notificationOccurred(style);
        break;
    }
  }

  /**
   * Tells Telegram
   */
  function ready(): void {
    WebApp.ready();
  }

  /**
   * Sets the header color of the app wrapper
   */
  function setHeaderColor(color: 'bg_color' | 'secondary_bg_color' | `#${string}`): void {
    WebApp.setHeaderColor(color);
  }

  /**
   * Sets the WebView background colour (Bot API 6.1+) — what Telegram paints
   * before the first frame and in iOS overscroll. No-op on older clients (the
   * SDK would only log a warning there).
   */
  function setBackgroundColor(color: 'bg_color' | 'secondary_bg_color' | `#${string}`): void {
    if (!WebApp.isVersionAtLeast('6.1')) return;
    WebApp.setBackgroundColor(color);
  }

  /**
   * Sets the colour of the bottom bar behind the native Main/Secondary buttons
   * (Bot API 7.10+). No-op on older clients.
   */
  function setBottomBarColor(color: 'bg_color' | 'secondary_bg_color' | 'bottom_bar_bg_color' | `#${string}`): void {
    if (!WebApp.isVersionAtLeast('7.10')) return;
    WebApp.setBottomBarColor(color);
  }

  function openTelegramLink(url: string): void {
    WebApp.openTelegramLink(url);
  }

  function openLink(url: string): void {
    WebApp.openLink(url);
  }

  function shareMessage(msgId: string, callback?: (success: boolean) => void): void {
    WebApp.shareMessage(msgId, callback);
  }

  function disableVerticalSwipes(): void {
    WebApp.disableVerticalSwipes();
  }

  /**
   * Ask Telegram to confirm before the user closes the Mini App (e.g. a
   * swipe-to-close). Used while a reading is mid-stream / unsaved.
   */
  function enableClosingConfirmation(): void {
    WebApp.enableClosingConfirmation();
  }

  function disableClosingConfirmation(): void {
    WebApp.disableClosingConfirmation();
  }

  /**
   * The current color scheme of the device. Can be light or dark.
   * If app is launched in a browser, the value will be undefined.
   */
  const colorScheme = WebApp.platform !== 'unknown' ? WebApp.colorScheme : undefined;

  /**
   * The current platform of the device.
   */
  const platform = WebApp.platform;

  /**
   * The current header color of the app wrapper
   */
  const headerColor = WebApp.headerColor;

  /**
   * The initial data received from Telegram
   */
  const webAppInitData = WebApp.initData || '';

  return {
    showMainButton,
    hideMainButton,
    setButtonLoader,
    showAlert,
    showConfirm,
    openInvoice,
    closeApp,
    expand,
    getViewportHeight,
    showBackButton,
    hideBackButton,
    vibrate,
    ready,
    colorScheme,
    platform,
    headerColor,
    setHeaderColor,
    setBackgroundColor,
    setBottomBarColor,
    webAppInitData,
    openTelegramLink,
    openLink,
    shareMessage,
    disableVerticalSwipes,
    enableClosingConfirmation,
    disableClosingConfirmation,
  };
}
